// Temporary API wrapper with fallback to original endpoints when optimized ones fail

import {
  fetchUserResearchOptimized,
  fetchPublicResearchOptimized,
  type ResearchListBucket,
  type ResearchListCounts,
  type UserResearchPaginatedResponse,
} from './optimizedApi'
import { fetchUserResearch, fetchPublicResearch } from './api'

const DEFAULT_RESEARCH_COUNTS: ResearchListCounts = {
  completed: 0,
  in_progress: 0,
  drafts: 0,
}

function isDraftLikeStudy(study: any): boolean {
  if (!study || typeof study !== 'object') return false
  return study.status === 'planning' || study.status === 'awaiting_confirmation'
}

function isInProgressStudy(study: any): boolean {
  if (!study || typeof study !== 'object') return false
  return [
    'pending',
    'queued',
    'in_progress',
    'running',
    'interrupted',
    'podcast_pending',
  ].includes(study.status)
}

function getFallbackCounts(research: any[]): ResearchListCounts {
  return {
    completed: research.filter(study => study?.status === 'completed').length,
    in_progress: research.filter(isInProgressStudy).length,
    drafts: research.filter(isDraftLikeStudy).length,
  }
}

function filterFallbackResearchByBucket(research: any[], bucket: ResearchListBucket): any[] {
  if (bucket === 'completed') {
    return research.filter(study => study?.status === 'completed')
  }
  if (bucket === 'in_progress') {
    return research.filter(isInProgressStudy)
  }
  if (bucket === 'drafts') {
    return research.filter(isDraftLikeStudy)
  }
  return research
}

export async function fetchUserResearchWithFallback(
  limit: number = 100,
  offset: number = 0,
  cacheBuster: string = '',
  bucket: ResearchListBucket = 'all'
): Promise<UserResearchPaginatedResponse<any>> {
  try {
    // Try optimized endpoint first
    // console.log('Trying optimized user research endpoint...')
    const response = await fetchUserResearchOptimized(limit, offset, true, cacheBuster, bucket)
    // console.log('Optimized endpoint successful')
    return response
  } catch (error) {
    console.warn('Optimized endpoint failed, falling back to original:', error)
    
    // Fallback to original endpoint
    try {
      const research = await fetchUserResearch()
      const filteredResearch = filterFallbackResearchByBucket(research, bucket)
      const counts = getFallbackCounts(research)
      // console.log('Fallback to original endpoint successful, got', research.length, 'items')
      // Convert to paginated response format
      return {
        total: filteredResearch.length,
        limit,
        offset,
        results: filteredResearch.slice(offset, offset + limit),
        has_more: offset + limit < filteredResearch.length,
        counts,
        bucket,
      }
    } catch (fallbackError) {
      console.error('Both endpoints failed:', fallbackError)
      return {
        total: 0,
        limit,
        offset,
        results: [],
        has_more: false,
        counts: DEFAULT_RESEARCH_COUNTS,
        bucket,
      }
    }
  }
}

export async function fetchPublicResearchWithFallback(limit: number = 100, offset: number = 0) {
  try {
    // Try optimized endpoint first
    // console.log('Trying optimized public research endpoint...')
    const response = await fetchPublicResearchOptimized(limit, offset)
    // console.log('Optimized endpoint successful')
    return response
  } catch (error) {
    console.warn('Optimized endpoint failed, falling back to original:', error)
    
    // Fallback to original endpoint
    try {
      const research = await fetchPublicResearch()
      // console.log('Fallback to original endpoint successful, got', research.length, 'items')
      // Convert to paginated response format
      return {
        total: research.length,
        limit,
        offset,
        results: research.slice(offset, offset + limit)
      }
    } catch (fallbackError) {
      console.error('Both endpoints failed:', fallbackError)
      return {
        total: 0,
        limit,
        offset,
        results: []
      }
    }
  }
}